import { componentText, SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { css, html, type PropertyValues } from 'lit'
import { property, state } from 'lit/decorators.js'
import { ReplaySubject, catchError, map, of, startWith, switchMap, takeUntil } from 'rxjs'
import type { FileViewerItem } from './file-viewer'
import { overflowWithin } from '@schmancy/directives/overflow-within'
import '../typography/typography'
import '../button/button'
import '@schmancy/form/fields/input/input'
import { csvRows } from './csv'
export { parseCsv } from './csv'

type CsvResult = { status: 'loading' | 'ready' | 'error'; rows: string[][] }

@schmancyElement('schmancy-csv-stage')
export class SchmancyCsvStage extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: grid;
				height: 100%;
				min-height: 0;
				grid-template-rows: auto minmax(0, 1fr);
			}
		`,
	]
	@property({ attribute: false }) file?: FileViewerItem
	@state() private result: CsvResult = { status: 'loading', rows: [] }
	@state() private query = ''
	@state() private wrap = false
	@state() private header = true
	@state() private sort: { column: number; direction: 1 | -1 } | undefined
	private files$ = new ReplaySubject<FileViewerItem>(1)

	override connectedCallback() {
		super.connectedCallback()
		this.files$
			.pipe(
				switchMap(file =>
					csvRows(file.previewUrl).pipe(
						map((rows): CsvResult => ({ status: 'ready', rows })),
						catchError(() => of<CsvResult>({ status: 'error', rows: [] })),
						startWith<CsvResult>({ status: 'loading', rows: [] }),
					),
				),
				takeUntil(this.disconnecting),
			)
			.subscribe(result => {
				this.result = result
			})
	}

	override willUpdate(changed: PropertyValues<this>) {
		if (changed.has('file') && this.file) {
			this.query = ''
			this.header = true
			this.wrap = false
			this.sort = undefined
			this.files$.next(this.file)
		}
	}

	override render() {
		if (this.result.status !== 'ready')
			return html` <div class="grid content-center justify-items-center gap-3 p-4" role="status" aria-live="polite">
				<schmancy-typography type="body" token="md"
					>${componentText(this.result.status === 'loading' ? 'csv.loading' : 'csv.failed')}</schmancy-typography
				>
				${
					this.result.status === 'error'
						? html`<schmancy-button
								@click=${() => {
									if (this.file) this.files$.next(this.file)
								}}
								>${componentText('csv.retry')}</schmancy-button
							>`
						: ''
				}
			</div>`
		if (!this.result.rows.length)
			return html`<schmancy-typography class="p-4" role="status" type="body" token="md"
				>${componentText('csv.empty')}</schmancy-typography
			>`
		const width = this.result.rows.reduce((max, row) => Math.max(max, row.length), 0)
		const rows = this.result.rows.map((cells, index) => ({ cells, number: index + 1 })).slice(this.header ? 1 : 0)
		const matches = rows.filter(row =>
			row.cells.some(cell => cell.toLocaleLowerCase().includes(this.query.toLocaleLowerCase())),
		)
		if (this.sort) {
			const { column, direction } = this.sort
			matches.sort(
				(a, b) =>
					direction * (a.cells[column] ?? '').localeCompare(b.cells[column] ?? '', undefined, { numeric: true }),
			)
		}
		return html`
			<div class="flex flex-wrap items-center gap-x-2 gap-y-1 px-2 py-1">
				<schmancy-input
					type="search"
					size="xs"
					variant="filled"
					class="min-w-0 flex-1"
					aria-label=${componentText('csv.search')}
					placeholder=${componentText('csv.search')}
					.value=${this.query}
					@input=${(event: CustomEvent<{ value: string }>) => {
						this.query = event.detail.value
					}}
				></schmancy-input>
				<schmancy-typography role="status" aria-live="polite" type="label" token="xs"
					>${componentText(this.query ? 'csv.matches' : 'csv.count', {
						count: matches.length,
						total: rows.length,
						columns: width,
					})}</schmancy-typography
				>
				<div class="flex flex-wrap items-center gap-2">
					<schmancy-button
						size="xs"
						.variant=${this.wrap ? 'filled tonal' : 'text'}
						.ariaPressed=${this.wrap ? 'true' : 'false'}
						@click=${() => {
							this.wrap = !this.wrap
						}}
						>${componentText('csv.wrap')}</schmancy-button
					>
					<schmancy-button
						size="xs"
						.variant=${this.header ? 'filled tonal' : 'text'}
						.ariaPressed=${this.header ? 'true' : 'false'}
						@click=${() => {
							this.header = !this.header
							this.sort = undefined
						}}
						>${componentText('csv.header')}</schmancy-button
					>
				</div>
			</div>
			<div
				${overflowWithin()}
				tabindex="0"
				role="region"
				aria-label=${this.file?.name ?? componentText('csv.preview')}
				@keydown=${(event: KeyboardEvent) => {
					if (event.target !== event.currentTarget || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return
					if (!(event.currentTarget instanceof HTMLElement)) return
					event.preventDefault()
					event.currentTarget.scrollBy({ left: event.key === 'ArrowRight' ? 80 : -80 })
				}}
			>
				<table class="w-full border-collapse" aria-label=${this.file?.name ?? componentText('csv.preview')}>
					<thead>
						<tr>
							<th
								scope="col"
								class="sticky top-0 left-0 z-20 bg-surface-container px-3 py-2"
								aria-label=${componentText('csv.row')}
							>
								#
							</th>
							${Array.from(
								{ length: width },
								(_, index) =>
									html` <th
										scope="col"
										class="sticky top-0 z-10 bg-surface-container px-3 py-2 text-start"
										aria-sort=${
											this.sort?.column === index ? (this.sort.direction === 1 ? 'ascending' : 'descending') : 'none'
										}
									>
										<schmancy-button
											class="whitespace-nowrap"
											variant="plain"
											size="xs"
											@click=${() => {
												this.sort =
													this.sort?.column === index && this.sort.direction === -1
														? undefined
														: {
																column: index,
																direction: this.sort?.column === index && this.sort.direction === 1 ? -1 : 1,
															}
											}}
										>
											${
												(this.header ? this.result.rows[0][index] : '') ||
												componentText('csv.column', { number: index + 1 })
											}
											<span aria-hidden="true"
												>${this.sort?.column === index ? (this.sort.direction === 1 ? ' ↑' : ' ↓') : ' ↕'}</span
											>
										</schmancy-button>
									</th>`,
							)}
						</tr>
					</thead>
					<tbody>
						${matches.map(
							row =>
								html` <tr class="even:bg-surface-containerLow hover:bg-surface-container">
									<th scope="row" class="sticky left-0 bg-surface-container px-3 py-2 align-top text-surface-onVariant">
										<schmancy-typography type="label" token="sm">${row.number}</schmancy-typography>
									</th>
									${Array.from(
										{ length: width },
										(_, index) =>
											html`<td class="px-3 py-2 align-top">
												<schmancy-typography
													class=${this.wrap ? 'whitespace-pre-wrap break-words min-w-32 max-w-80' : 'whitespace-pre'}
													type="body"
													token="sm"
													>${row.cells[index] ?? ''}</schmancy-typography
												>
											</td>`,
									)}
								</tr>`,
						)}
					</tbody>
				</table>
				${
					!matches.length && this.query
						? html`<div class="grid justify-items-center gap-2 p-6" role="status">
								<schmancy-typography type="body" token="md">${componentText('csv.noMatches')}</schmancy-typography
								><schmancy-button
									@click=${() => {
										this.shadowRoot?.querySelector('schmancy-input')?.focus()
										this.query = ''
									}}
									>${componentText('csv.clear')}</schmancy-button
								>
							</div>`
						: ''
				}
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-csv-stage': SchmancyCsvStage
	}
}
